import { keepPreviousData, queryOptions, useQuery } from "@tanstack/react-query";
import { DateRange } from "react-day-picker";

import * as apiClient from '@/lib/api'
import {Role, ROLES} from "@/lib/auth/roles";
import { formatDate } from "@/lib/filters/date/date-utils";
import useIsQueryEnabled from "@/lib/hooks/query-enabler";
import { useQueryResetWithRefresh } from "@/lib/hooks/query-refetch-with-reset";
import { GeneralPerformance } from "@/modules/general-performance/types";
import { useDate } from "@/stores/date-filter";
import { useToken } from "@/stores/token";
import { DEFAULT_DATEFORMAT } from "@/utils/constants";
import { QUERY_KEY_PERFORMANCE_BY_ADVISER_SCORES } from "@/utils/keys";

function fetchProfessionalPerformance(date: DateRange): Promise<GeneralPerformance> {
  const to = formatDate(date.to, DEFAULT_DATEFORMAT)
  const from = formatDate(date.from, DEFAULT_DATEFORMAT)
    
  return apiClient.get(`/firm/professional/performance/table?to=${to}&from=${from}`)
}

export const performanceByAdviserQueryOptions = (
  date: DateRange,
) => queryOptions({
  queryKey: [QUERY_KEY_PERFORMANCE_BY_ADVISER_SCORES, date],
  queryFn: () => fetchProfessionalPerformance(date),
  placeholderData: keepPreviousData,
})

export const useProfessionalPerformance = () => {
  const { date } = useDate()
  const { token } = useToken()

  const roles: Role[] | undefined = token?.roles;
  const queryOptions = performanceByAdviserQueryOptions(date)

  const targetRoles: Role[] = [ROLES.ROLE_VIEW_ADVISER, ROLES.ROLE_VIEW_FIRM];
  const permission: boolean = !!roles?.some(role => targetRoles.includes(role));

  queryOptions.enabled = useIsQueryEnabled(permission)

  const query = useQuery(queryOptions)
  const { resetAndRefetch } = useQueryResetWithRefresh(queryOptions.queryKey, query.refetch)

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    isRefetchError: query.isRefetchError,
    error: query.error,
    isRefetching: query.isRefetching,
    isSuccess: query.isSuccess,
    status: query.status,
    refetch: resetAndRefetch
  }
}